import { useMemo, useState } from 'react';

import { notifications } from '@mantine/notifications';

import { useAppSelector } from '@/store/hooks';
import { useUpdatePatientMutation } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';
import { usePatchTransmissionReportMutation } from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';

import Dropdown from '@/ui/Dropdown';
import LabeledField from '@/ui/LabeledField';

interface PractitionerSelectProps {
  label: string;
  value: string | null;
  patientId: string;
  reportId?: number;
  onlyOnReport?: boolean;
  isReportEditMode?: boolean;
  patientPractitioners: Array<{ id: string; given: string; family: string }> | undefined;
  isPatientPractitionersLoading: boolean;
  updateField: { patient?: string; report?: string };
  onUpdate: (value: string | null, source?: 'edit' | 'revert') => void;
  labelVariant?: 'allCaps' | 'regular' | 'sentence';
  editOnHover?: boolean;
}

const PractitionerSelect = ({
  label,
  value,
  patientId,
  reportId,
  onlyOnReport = false,
  isReportEditMode = false,
  patientPractitioners,
  isPatientPractitionersLoading,
  updateField,
  onUpdate,
  labelVariant = 'allCaps',
  editOnHover = false
}: PractitionerSelectProps) => {
  const [updatePatient, { isLoading: isUpdatingPatient }] = useUpdatePatientMutation();
  const [updateReport, { isLoading: isUpdatingReport }] = usePatchTransmissionReportMutation();

  const { isActiveReportFinalSigned: isReportFinalSigned } = useAppSelector(
    (state) => state.transmissionReportForm
  );

  const [isEditing, setIsEditing] = useState(false);

  const practitionerOptions = useMemo(() => {
    return (
      patientPractitioners?.map((practitioner) => ({
        label: `${practitioner.given} ${practitioner.family}`,
        value: practitioner.id
      })) ?? []
    );
  }, [patientPractitioners]);

  const selectedPractitioner = useMemo(() => {
    return patientPractitioners?.find((practitioner) => practitioner.id === value);
  }, [patientPractitioners, value]);

  const handleChange = async (newValue: string | null) => {
    try {
      onUpdate(newValue, 'edit');

      if (reportId && (onlyOnReport || isReportFinalSigned) && updateField.report) {
        await updateReport({
          id: reportId,
          [updateField.report]: newValue === '' ? null : newValue
        }).unwrap();
      } else if (updateField.patient) {
        await updatePatient({
          patient: {
            id: patientId,
            [updateField.patient]: {
              id: newValue === '' ? null : newValue
            }
          },
          activeReportId: reportId
        }).unwrap();
      }

      notifications.show({
        title: 'Success',
        message: `${label} updated successfully`,
        color: 'green'
      });

      setIsEditing(false);
    } catch (error) {
      // Revert the state if the update fails
      onUpdate(value, 'revert');

      notifications.show({
        title: 'Error',
        message: `Failed to update ${label.toLowerCase()}. Please try again.`,
        color: 'red'
      });

      console.error(`Error updating ${label.toLowerCase()}:`, error);
    }
  };

  const isUpdating = isUpdatingPatient || isUpdatingReport;

  const renderPractitionerSelect = () => {
    return (
      <Dropdown
        isMulti={false}
        options={practitionerOptions}
        value={practitionerOptions?.find((option) => option.value === value)}
        isDisabled={isPatientPractitionersLoading}
        onChange={(option) => {
          if (option) {
            handleChange(option.value);
          }
        }}
        showClear={true}
      />
    );
  };

  const practitionerName = selectedPractitioner
    ? `${selectedPractitioner.given} ${selectedPractitioner.family}`
    : 'Not assigned';

  return (
    <>
      <LabeledField
        label={label}
        value={practitionerName}
        labelVariant={labelVariant}
        editOnHover={editOnHover}
        showEditIcon={isReportEditMode}
        onEdit={() => setIsEditing(!isEditing)}
        isLoading={isUpdating}
        isEditable={isEditing}
        customEditableComponent={renderPractitionerSelect()}
      />
    </>
  );
};

export default PractitionerSelect;
